/**
 * Capacitor-aware platform bootstrap. Safe to import on the web build:
 * all Capacitor imports are dynamic + guarded, and every function
 * no-ops on the web where `Capacitor` isn't available.
 *
 * Why dynamic imports? `@busswerks/mobile` is the only
 * workspace that depends on `@capacitor/*`. The web build
 * (`@busswerks/app`) needs to compile without those packages
 * installed locally when developers haven't run
 * `npm install -w @busswerks/mobile`. Dynamic `import()`
 * calls are gated by a runtime `isNative` check so Vite tree-shakes
 * them away for the web bundle.
 */

let cachedIsNative: boolean | null = null

/**
 * True when the SPA is running inside a Capacitor WebView (iOS /
 * iPadOS, also reached on Apple Silicon Mac + Vision Pro via
 * "Designed for iPad"). Detected via the `Capacitor` global that
 * the native shell injects into the page before our bundle loads.
 */
export function isNativePlatform(): boolean {
  if (cachedIsNative !== null) return cachedIsNative
  if (typeof window === 'undefined') {
    cachedIsNative = false
    return false
  }
  const cap = (
    window as unknown as {
      Capacitor?: {
        isNativePlatform?: () => boolean
        getPlatform?: () => string
      }
    }
  ).Capacitor
  cachedIsNative = !!cap?.isNativePlatform?.()
  return cachedIsNative
}

/**
 * Returns the low-level platform identifier. `'web'` on web,
 * `'ios'` when running inside Capacitor (Apple reports iPhone,
 * iPad, and "Designed for iPad" on Mac / Vision Pro all as
 * `'ios'`). We split iPhone from iPad via UA inside `usePlatform`.
 */
export function getCapacitorPlatform(): 'web' | 'ios' | 'android' {
  if (typeof window === 'undefined') return 'web'
  const cap = (
    window as unknown as {
      Capacitor?: { getPlatform?: () => string }
    }
  ).Capacitor
  const platform = cap?.getPlatform?.() ?? 'web'
  return platform === 'ios' || platform === 'android' ? platform : 'web'
}

/**
 * Configure the iOS status bar, hide the splash screen, and register
 * keyboard listeners. Called once from `main.tsx` before React mounts.
 * No-ops on web.
 */
export async function initNativeShell(): Promise<void> {
  if (!isNativePlatform()) return

  // Tag the document so CSS can gate native-only rules (e.g. the
  // `.pt-safe` floor in globals.css that keeps Mac Catalyst's
  // title bar + traffic lights from overlapping our header — Mac
  // Catalyst doesn't populate `env(safe-area-inset-top)` the way
  // iOS does).
  document.documentElement.classList.add('is-native')

  try {
    const [{ StatusBar, Style }, { SplashScreen }, { Keyboard }] =
      await Promise.all([
        import('@capacitor/status-bar'),
        import('@capacitor/splash-screen'),
        import('@capacitor/keyboard'),
      ])

    // The editor uses a dark header by default, so we want light
    // status bar content (white glyphs against our dark chrome).
    //
    // `setOverlaysWebView({ overlay: true })` lets the webview cover
    // the full screen under the status bar / Dynamic Island. Content
    // stays out of the way via CSS `env(safe-area-inset-top)` (see
    // `.pt-safe` in globals.css, wired up in every top-level chrome
    // component). This is the modern iOS pattern and the only one
    // that plays nicely with `viewport-fit=cover` in index.html.
    //
    // The earlier `overlay: false` variant asked UIKit to reserve an
    // opaque strip for the status bar — that silently conflicted with
    // `viewport-fit=cover`, leaving safe-area insets at 0 and content
    // drawing under the notch. Don't flip this back without also
    // removing `viewport-fit=cover`.
    await StatusBar.setStyle({ style: Style.Light }).catch(() => {})
    await StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {})

    // Hide the splash screen after React mounts. We request the fade
    // with a small delay to avoid a flash of unstyled content.
    setTimeout(() => {
      SplashScreen.hide({ fadeOutDuration: 150 }).catch(() => {})
    }, 50)

    // Tag the document when the keyboard is open so CSS can react.
    // Unused for now but cheap to wire — gives us the hook for
    // dialog scroll-into-view if we need it later.
    Keyboard.addListener('keyboardWillShow', () => {
      document.documentElement.dataset.keyboardOpen = 'true'
    })
    Keyboard.addListener('keyboardWillHide', () => {
      delete document.documentElement.dataset.keyboardOpen
    })
  } catch (err) {
    // Non-fatal — native plugins may be missing during a hot reload
    // or in the simulator's first launch. Log and continue so the
    // web UI still renders.

    console.warn('[nativePlatform] initNativeShell failed:', err)
  }
}
